import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  CheckboxField,
  DatePickerField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

// Zod schema: make fields optional and nullable to match the form behavior
const FormSchema = z.object({
  issuer_id: z.coerce.number().optional().nullable(),
  issuer_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),

      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  exception: z.boolean().optional().nullable(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export function F1IssuersExclusionEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    disable_translations_request: true,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Issuer Exclusion {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="issuer_name"
                  label={t(`${table_name}.columns.issuer_name`, {
                    ns: "tables",
                    defaultValue: "Issuer",
                  })}
                  isRequired={true}
                  control={form.control}
                  base_url={`${Clients.F1_QUANTILIA}/${Tables.F1_ISSUERS_EXCLUSION}`}
                  id_column="issuer_id"
                  column_name="issuer_name"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.issuer_name`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <CheckboxField
                    name="exception"
                    label={t(`${table_name}.columns.exception`, {
                      ns: "tables",
                      defaultValue: "Is Exception",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
